import {
  get_pin_value,
  get_pin_role,
  get_pin_capabilities,
} from './pinCompatibility';
import {
  getSevenSegmentDigitPinKeys,
  normalizeSevenSegmentDigitCount,
  SEVEN_SEGMENT_SEGMENT_PIN_KEYS,
} from './sevenSegmentDisplay';
import {
  BREADBOARD_PIN_KEYS,
  BREADBOARD_TEMPLATE_TYPE,
  getBreadboardBusKeyForPinKey,
  getBreadboardDisplayNameForPinKey,
} from './projectBuilderBreadboard';

const LCD_16X2_PIN_DEFINITIONS = Object.freeze([
  {
    pinKey: 'VSS',
    displayName: 'Ground',
    pinRole: 'lcd-ground',
    role: 'ground',
    capabilityTags: ['lcd-ground', 'ground', 'passive'],
    electricalType: 'ground',
    direction: 'input',
  },
  {
    pinKey: 'VDD',
    displayName: 'VDD',
    pinRole: 'lcd-power',
    role: 'power',
    capabilityTags: ['lcd-power', 'power', '5v', 'passive'],
    electricalType: 'power',
    direction: 'input',
  },
  {
    pinKey: 'V0',
    displayName: 'Contrast',
    pinRole: 'lcd-contrast',
    role: 'analog',
    capabilityTags: ['lcd-contrast', 'analog-input', 'passive'],
    electricalType: 'input',
    direction: 'input',
  },
  {
    pinKey: 'RS',
    displayName: 'RS',
    pinRole: 'lcd-register-select',
    role: 'input',
    capabilityTags: ['lcd-register-select', 'digital-input', 'passive'],
    electricalType: 'input',
    direction: 'input',
  },
  {
    pinKey: 'RW',
    displayName: 'RW',
    pinRole: 'lcd-read-write',
    role: 'input',
    capabilityTags: ['lcd-read-write', 'digital-input', 'passive'],
    electricalType: 'input',
    direction: 'input',
  },
  {
    pinKey: 'E',
    displayName: 'Enable',
    pinRole: 'lcd-enable',
    role: 'input',
    capabilityTags: ['lcd-enable', 'digital-input', 'passive'],
    electricalType: 'input',
    direction: 'input',
  },
  ...['D0', 'D1', 'D2', 'D3', 'D4', 'D5', 'D6', 'D7'].map((pinKey) => ({
    pinKey,
    displayName: pinKey,
    pinRole: 'lcd-data',
    role: 'input',
    capabilityTags: ['lcd-data', 'digital-input', 'passive'],
    electricalType: 'input',
    direction: 'input',
  })),
  {
    pinKey: 'A',
    displayName: 'Backlight +',
    pinRole: 'lcd-backlight-anode',
    role: 'power',
    capabilityTags: ['lcd-backlight-anode', 'power', 'passive'],
    electricalType: 'input',
    direction: 'input',
  },
  {
    pinKey: 'K',
    displayName: 'Backlight -',
    pinRole: 'lcd-backlight-cathode',
    role: 'ground',
    capabilityTags: ['lcd-backlight-cathode', 'ground', 'passive'],
    electricalType: 'input',
    direction: 'input',
  },
]);

const SEVEN_SEGMENT_SEGMENT_DISPLAY_NAMES = Object.freeze({
  A: 'Segment A',
  B: 'Segment B',
  C: 'Segment C',
  D: 'Segment D',
  E: 'Segment E',
  F: 'Segment F',
  G: 'Segment G',
  DP: 'Decimal Point',
});

const buildSevenSegmentPinDefinitions = (digitCount) => {
  const safeDigitCount = normalizeSevenSegmentDigitCount(digitCount);

  return [
    ...SEVEN_SEGMENT_SEGMENT_PIN_KEYS.map((pinKey) => ({
      pinKey,
      displayName: SEVEN_SEGMENT_SEGMENT_DISPLAY_NAMES[pinKey] || pinKey,
      pinRole: 'seven-segment-segment',
      role: 'input',
      capabilityTags: ['seven-segment-segment', 'digital-input', 'passive'],
      electricalType: 'input',
      direction: 'input',
    })),
    ...getSevenSegmentDigitPinKeys(safeDigitCount).map((pinKey, index) => ({
      pinKey,
      displayName: `Digit ${index + 1} Common`,
      pinRole: 'seven-segment-digit-common',
      role: 'input',
      capabilityTags: ['seven-segment-digit-common', 'digital-input', 'passive'],
      electricalType: 'input',
      direction: 'input',
    })),
  ];
};

const get_board_pins = (board) => {
  const candidates = [
    board?.pins,
    board?.board_pins,
    board?.boardPins,
    board?.pinout,
  ];

  return candidates.find(Array.isArray) || [];
};

export const get_display_pin_name = (pin) => {
  return (
    get_pin_value(pin, ['pin_name', 'pinName']) ||
    get_pin_value(pin, ['pin_number', 'pinNumber']) ||
    get_pin_value(pin, ['name']) ||
    'Pin'
  );
};

export const get_component_suggestion_pins = (component) => {
  if (!component) return [];

  if (component.type === 'board') {
    const boardPins = get_board_pins(component.board);

  return boardPins.map((pin, index) => {
    const pin_name = get_display_pin_name(pin);

    const nearbyPins = boardPins.slice(
      Math.max(0, index - 3),
      Math.min(boardPins.length, index + 4)
    );

    const pin_context_text = nearbyPins
      .map((nearbyPin) => [
        get_pin_value(nearbyPin, ['pin_name', 'pinName']),
        get_pin_value(nearbyPin, ['description']),
        get_pin_value(nearbyPin, ['functions']),
        get_pin_value(nearbyPin, ['alternate_functions', 'alternateFunctions']),
      ].filter(Boolean).join(' '))
      .filter(Boolean)
      .join(' ')
      .toLowerCase();

    return {
      ...pin,

      component_id: component.id,
      component_name: component.board?.name || component.name,
      component_type: 'board',

      marker_key:
        get_pin_value(pin, ['svg_id', 'svgId']) ||
        get_pin_value(pin, ['pin_name', 'pinName']) ||
        get_pin_value(pin, ['pin_number', 'pinNumber']) ||
        String(pin.id),

      display_name: pin_name,
      role: get_pin_role(pin),
      capabilities: get_pin_capabilities(pin),

      // Used by the suggestion engine to avoid service-header power/GND pins.
      pin_context_text,
    };
   });
  }

  if (component.type === 'passive' && component.passiveType === 'resistor') {
    return [
      {
        id: `${component.id}:A`,
        component_id: component.id,
        component_name: component.name || 'Resistor',
        component_type: 'passive',
        passive_type: 'resistor',
        marker_key: 'A',
        pin_name: 'A',
        display_name: 'A',
        pin_role: 'resistor-terminal',
        capability_tags: ['resistor-terminal', 'passive'],
        resistance_ohms: component.valueOhms,
        component_value_ohms: component.valueOhms,
        led_color: component.color || '#E60000',
        component_color: component.color || '#E60000',
      },
      {
        id: `${component.id}:B`,
        component_id: component.id,
        component_name: component.name || 'Resistor',
        component_type: 'passive',
        passive_type: 'resistor',
        marker_key: 'B',
        pin_name: 'B',
        display_name: 'B',
        pin_role: 'resistor-terminal',
        capability_tags: ['resistor-terminal', 'passive'],
        resistance_ohms: component.valueOhms,
        component_value_ohms: component.valueOhms,
        led_color: component.color || '#E60000',
        component_color: component.color || '#E60000',  
      },
    ];
  }

  if (component.type === 'passive' && component.passiveType === 'led') {
    return [
      {
        id: `${component.id}:A`,
        component_id: component.id,
        component_name: component.name || 'LED',
        component_type: 'passive',
        passive_type: 'led',
        marker_key: 'A',
        pin_name: 'A',
        display_name: 'Anode',
        pin_role: 'led-anode',
        capability_tags: ['led-anode', 'diode-anode', 'passive'],
      },
      {
        id: `${component.id}:K`,
        component_id: component.id,
        component_name: component.name || 'LED',
        component_type: 'passive',
        passive_type: 'led',
        marker_key: 'K',
        pin_name: 'K',
        display_name: 'Cathode',
        pin_role: 'led-cathode',
        capability_tags: ['led-cathode', 'diode-cathode', 'passive'],
      },
    ];
  }

  if (component.type === 'passive' && component.passiveType === 'dc_power') {
    return [
      {
        id: `${component.id}:+`,
        component_id: component.id,
        component_name: component.name || '5 V Source',
        component_type: 'passive',
        passive_type: 'dc_power',
        marker_key: '+',
        pin_name: '+',
        display_name: 'Positive 5 V',
        pin_role: 'dc-positive',
        role: 'power',
        capability_tags: ['power-source', '5v', 'positive', 'passive'],
        electrical_type: 'power',
        direction: 'output',
      },
      {
        id: `${component.id}:-`,
        component_id: component.id,
        component_name: component.name || '5 V Source',
        component_type: 'passive',
        passive_type: 'dc_power',
        marker_key: '-',
        pin_name: '-',
        display_name: 'Ground',
        pin_role: 'dc-ground',
        role: 'ground',
        capability_tags: ['power-source', 'ground', 'passive'],
        electrical_type: 'ground',
        direction: 'output',
      },
    ];
  }

  if (component.type === 'passive' && component.passiveType === 'push_button') {
    return [
      {
        id: `${component.id}:1`,
        component_id: component.id,
        component_name: component.name || 'Push Button',
        component_type: 'passive',
        passive_type: 'push_button',
        marker_key: '1',
        pin_name: '1',
        display_name: 'Terminal 1',
        pin_role: 'switch-terminal',
        role: 'switch',
        capability_tags: ['switch-terminal', 'passive', 'digital-input'],
        electrical_type: 'passive',
        direction: 'bidirectional',
      },
      {
        id: `${component.id}:2`,
        component_id: component.id,
        component_name: component.name || 'Push Button',
        component_type: 'passive',
        passive_type: 'push_button',
        marker_key: '2',
        pin_name: '2',
        display_name: 'Terminal 2',
        pin_role: 'switch-terminal',
        role: 'switch',
        capability_tags: ['switch-terminal', 'passive', 'digital-input'],
        electrical_type: 'passive',
        direction: 'bidirectional',
      },
    ];
  }

  if (component.type === 'passive' && component.passiveType === 'potentiometer') {
    return [
      {
        id: `${component.id}:A`,
        component_id: component.id,
        component_name: component.name || 'Potentiometer',
        component_type: 'passive',
        passive_type: 'potentiometer',
        marker_key: 'A',
        pin_name: 'A',
        display_name: 'End A',
        pin_role: 'potentiometer-end',
        role: 'passive',
        capability_tags: ['potentiometer-end', 'variable-resistor', 'passive'],
        resistance_ohms: component.valueOhms,
        component_value_ohms: component.valueOhms,
        electrical_type: 'passive',
        direction: 'bidirectional',
      },
      {
        id: `${component.id}:W`,
        component_id: component.id,
        component_name: component.name || 'Potentiometer',
        component_type: 'passive',
        passive_type: 'potentiometer',
        marker_key: 'W',
        pin_name: 'W',
        display_name: 'Wiper',
        pin_role: 'potentiometer-wiper',
        role: 'analog',
        capability_tags: ['potentiometer-wiper', 'analog-signal', 'variable-output', 'passive'],
        resistance_ohms: component.valueOhms,
        component_value_ohms: component.valueOhms,
        electrical_type: 'passive',
        direction: 'bidirectional',
      },
      {
        id: `${component.id}:B`,
        component_id: component.id,
        component_name: component.name || 'Potentiometer',
        component_type: 'passive',
        passive_type: 'potentiometer',
        marker_key: 'B',
        pin_name: 'B',
        display_name: 'End B',
        pin_role: 'potentiometer-end',
        role: 'passive',
        capability_tags: ['potentiometer-end', 'variable-resistor', 'passive'],
        resistance_ohms: component.valueOhms,
        component_value_ohms: component.valueOhms,
        electrical_type: 'passive',
        direction: 'bidirectional',
      },
    ];
  }

  if (component.type === 'passive' && component.passiveType === 'buzzer') {
  return [
    {
      id: `${component.id}:+`,
      component_id: component.id,
      component_name: component.name || 'Buzzer',
      component_type: 'passive',
      passive_type: 'buzzer',
      marker_key: '+',
      pin_name: '+',
      display_name: 'Positive',
      pin_role: 'buzzer-positive',
      role: 'output',
      capability_tags: ['buzzer-positive', 'sound-output', 'digital-output', 'pwm-output', 'passive'],
      electrical_type: 'load',
      direction: 'input',
    },
    {
      id: `${component.id}:-`,
      component_id: component.id,
      component_name: component.name || 'Buzzer',
      component_type: 'passive',
      passive_type: 'buzzer',
      marker_key: '-',
      pin_name: '-',
      display_name: 'Negative',
      pin_role: 'buzzer-negative',
      role: 'ground',
      capability_tags: ['buzzer-negative', 'ground', 'passive'],
      electrical_type: 'return',
      direction: 'bidirectional',
    },
  ];
}

  if (component.type === 'passive' && component.passiveType === 'servo') {
    return [
      {
        id: `${component.id}:SIG`,
        component_id: component.id,
        component_name: component.name || 'Servo',
        component_type: 'passive',
        passive_type: 'servo',
        marker_key: 'SIG',
        pin_name: 'SIG',
        display_name: 'Signal',
        pin_role: 'servo-signal',
        role: 'input',
        capability_tags: ['servo-signal', 'pwm-input', 'digital-input', 'passive'],
        electrical_type: 'input',
        direction: 'input',
      },
      {
        id: `${component.id}:V+`,
        component_id: component.id,
        component_name: component.name || 'Servo',
        component_type: 'passive',
        passive_type: 'servo',
        marker_key: 'V+',
        pin_name: 'V+',
        display_name: 'V+',
        pin_role: 'servo-power',
        role: 'power',
        capability_tags: ['servo-power', 'power', '5v', 'passive'],
        electrical_type: 'power',
        direction: 'input',
      },
      {
        id: `${component.id}:GND`,
        component_id: component.id,
        component_name: component.name || 'Servo',
        component_type: 'passive',
        passive_type: 'servo',
        marker_key: 'GND',
        pin_name: 'GND',
        display_name: 'Ground',
        pin_role: 'servo-ground',
        role: 'ground',
        capability_tags: ['servo-ground', 'ground', 'passive'],
        electrical_type: 'ground',
        direction: 'input',
      },
    ];
  }

  if (component.type === 'passive' && component.passiveType === 'lcd_16x2') {
    return LCD_16X2_PIN_DEFINITIONS.map((pinDefinition) => ({
      id: `${component.id}:${pinDefinition.pinKey}`,
      component_id: component.id,
      component_name: component.name || 'LCD 16x2',
      component_type: 'passive',
      passive_type: 'lcd_16x2',
      marker_key: pinDefinition.pinKey,
      pin_name: pinDefinition.pinKey,
      display_name: pinDefinition.displayName,
      pin_role: pinDefinition.pinRole,
      role: pinDefinition.role,
      capability_tags: pinDefinition.capabilityTags,
      electrical_type: pinDefinition.electricalType,
      direction: pinDefinition.direction,
    }));
  }

  if (component.type === 'passive' && component.passiveType === 'seven_segment_display') {
    return buildSevenSegmentPinDefinitions(component.digitCount).map((pinDefinition) => ({
      id: `${component.id}:${pinDefinition.pinKey}`,
      component_id: component.id,
      component_name: component.name || '7-Segment Display',
      component_type: 'passive',
      passive_type: 'seven_segment_display',
      marker_key: pinDefinition.pinKey,
      pin_name: pinDefinition.pinKey,
      display_name: pinDefinition.displayName,
      pin_role: pinDefinition.pinRole,
      role: pinDefinition.role,
      capability_tags: pinDefinition.capabilityTags,
      electrical_type: pinDefinition.electricalType,
      direction: pinDefinition.direction,
    }));
  }

  if (component.type === 'passive' && component.passiveType === BREADBOARD_TEMPLATE_TYPE) {
    return BREADBOARD_PIN_KEYS.map((pinKey) => ({
      id: `${component.id}:${pinKey}`,
      component_id: component.id,
      component_name: component.name || 'Breadboard',
      component_type: 'passive',
      passive_type: BREADBOARD_TEMPLATE_TYPE,
      marker_key: pinKey,
      pin_name: pinKey,
      display_name: getBreadboardDisplayNameForPinKey(pinKey),
      pin_role: 'breadboard-hole',
      role: 'passive',
      capability_tags: ['breadboard-hole', 'passive'],
      electrical_type: 'passive',
      direction: 'bidirectional',
      breadboard_bus_key: getBreadboardBusKeyForPinKey(pinKey),
    }));
  }

  if (component.type === 'passive' && component.passiveType === 'generic_2pin') {
    return [
      {
        id: `${component.id}:1`,
        component_id: component.id,
        component_name: component.name || 'Basic Part',
        component_type: 'passive',
        passive_type: 'generic_2pin',
        marker_key: '1',
        pin_name: '1',
        display_name: 'Pin 1',
        pin_role: 'passive',
        role: 'passive',
        capability_tags: ['passive'],
        direction: 'bidirectional',
      },
      {
        id: `${component.id}:2`,
        component_id: component.id,
        component_name: component.name || 'Basic Part',
        component_type: 'passive',
        passive_type: 'generic_2pin',
        marker_key: '2',
        pin_name: '2',
        display_name: 'Pin 2',
        pin_role: 'passive',
        role: 'passive',
        capability_tags: ['passive'],
        direction: 'bidirectional',
      },
    ];
  }

  if (component.type === 'passive' && component.passiveType === 'generic_3pin') {
    return [
      {
        id: `${component.id}:1`,
        component_id: component.id,
        component_name: component.name || 'Basic Part',
        component_type: 'passive',
        passive_type: 'generic_3pin',
        marker_key: '1',
        pin_name: '1',
        display_name: 'Pin 1',
        pin_role: 'passive',
        role: 'passive',
        capability_tags: ['passive'],
        direction: 'bidirectional',
      },
      {
        id: `${component.id}:2`,
        component_id: component.id,
        component_name: component.name || 'Basic Part',
        component_type: 'passive',
        passive_type: 'generic_3pin',
        marker_key: '2',
        pin_name: '2',
        display_name: 'Pin 2',
        pin_role: 'passive',
        role: 'passive',
        capability_tags: ['passive'],
        direction: 'bidirectional',
      },
      {
        id: `${component.id}:3`,
        component_id: component.id,
        component_name: component.name || 'Basic Part',
        component_type: 'passive',
        passive_type: 'generic_3pin',
        marker_key: '3',
        pin_name: '3',
        display_name: 'Pin 3',
        pin_role: 'passive',
        role: 'passive',
        capability_tags: ['passive'],
        direction: 'bidirectional',
      },
    ];
  }

  return [];
};

export const get_all_project_suggestion_pins = (components) => {
  return (Array.isArray(components) ? components : []).flatMap((component) =>
    get_component_suggestion_pins(component)
  );
};

export const get_component_suggestion_pin_map = (components) => {
  const map = new Map();

  get_all_project_suggestion_pins(components).forEach((pin) => {
    map.set(String(pin.id), pin);
  });

  return map;
};

export const strip_marker_index = (marker_key) => {
  return String(marker_key || '').replace(/__\d+$/, '');
};

const normalize_match_text = (value) => {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '');
};

export const find_marker_key_for_pin = (pin, marker_keys) => {
  const safe_marker_keys = Array.isArray(marker_keys) ? marker_keys : [];

  if (!pin || safe_marker_keys.length === 0) {
    return '';
  }

  const pin_name = normalize_match_text(
    get_pin_value(pin, ['pin_name', 'pinName'])
  );

  const pin_number = normalize_match_text(
    get_pin_value(pin, ['pin_number', 'pinNumber'])
  );

  const svg_id = normalize_match_text(
    get_pin_value(pin, ['svg_id', 'svgId'])
  );

  const gpio_name = normalize_match_text(
    get_pin_value(pin, ['gpio_name', 'gpioName'])
  );

  const candidates = [
    svg_id,
    pin_name,
    pin_number,
    gpio_name,
  ].filter(Boolean);

  // Exact match against marker key base: D1__10 -> D1
  const exact_match = safe_marker_keys.find((marker_key) => {
    const marker_base = normalize_match_text(strip_marker_index(marker_key));
    return candidates.includes(marker_base);
  });

  if (exact_match) {
    return exact_match;
  }

  // Looser fallback: useful when marker uses GPIO5 but API pin name is D1,
  // or when SVG ids are slightly longer.
  const loose_match = safe_marker_keys.find((marker_key) => {
    const marker_base = normalize_match_text(strip_marker_index(marker_key));

    return candidates.some((candidate) =>
      candidate &&
      marker_base &&
      (marker_base.includes(candidate) || candidate.includes(marker_base))
    );
  });

  return loose_match || '';
};

const get_marker_base = (marker_key) => {
  return normalize_match_text(strip_marker_index(marker_key));
};

const get_pin_match_candidates = (pin) => {
  return [
    get_pin_value(pin, ['svg_id', 'svgId']),
    get_pin_value(pin, ['pin_name', 'pinName']),
    get_pin_value(pin, ['pin_number', 'pinNumber']),
    get_pin_value(pin, ['gpio_name', 'gpioName']),
  ]
    .map(normalize_match_text)
    .filter(Boolean);
};

const find_unused_marker_key_for_pin = (pin, marker_keys, used_marker_keys) => {
  const safe_marker_keys = Array.isArray(marker_keys) ? marker_keys : [];
  const candidates = get_pin_match_candidates(pin);

  if (!pin || safe_marker_keys.length === 0 || candidates.length === 0) {
    return '';
  }

  const unused_marker_keys = safe_marker_keys.filter(
    marker_key => !used_marker_keys.has(marker_key)
  );

  const exact_match = unused_marker_keys.find((marker_key) => {
    const marker_base = get_marker_base(marker_key);
    return candidates.includes(marker_base);
  });

  if (exact_match) {
    return exact_match;
  }

  const loose_match = unused_marker_keys.find((marker_key) => {
    const marker_base = get_marker_base(marker_key);

    return candidates.some((candidate) =>
      candidate &&
      marker_base &&
      (marker_base.includes(candidate) || candidate.includes(marker_base))
    );
  });

  return loose_match || '';
};

export const get_project_pins_with_marker_keys = (components, pin_layout_by_part_id) => {
  const all_pins = get_all_project_suggestion_pins(components);
  const used_marker_keys_by_component = new Map();

  return all_pins.map((pin) => {
    const component_id = pin.component_id;
    const marker_keys = Object.keys(pin_layout_by_part_id?.[component_id] || {});

    if (!used_marker_keys_by_component.has(component_id)) {
      used_marker_keys_by_component.set(component_id, new Set());
    }

    const used_marker_keys = used_marker_keys_by_component.get(component_id);

    const resolved_marker_key = find_unused_marker_key_for_pin(
      pin,
      marker_keys,
      used_marker_keys
    );

    if (resolved_marker_key) {
      used_marker_keys.add(resolved_marker_key);
    }

    return {
      ...pin,
      marker_key: resolved_marker_key || pin.marker_key || '',
      has_canvas_marker: Boolean(resolved_marker_key),
    };
  });
};
